<div class="space-y-6">
  @if (loading) {
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div class="daisy-skeleton h-44 w-full rounded-2xl"></div>
      <div class="daisy-skeleton h-44 w-full rounded-2xl"></div>
    </div>
  } @else if (quizzes.length === 0) {
    <div class="flex flex-col items-center justify-center py-16 px-4">
      <div class="bg-base-200 rounded-full p-6 mb-4">
        <i class="fa-solid fa-clipboard-question text-4xl text-base-content/40"></i>
      </div>
      <h3 class="text-xl font-semibold text-base-content mb-2">
        No Quizzes Available
      </h3>
      <p class="text-base-content/60 text-center max-w-md">
        Your instructor has not published any quizzes for this class yet.
      </p>
    </div>
  } @else {
    <!-- Quiz List Grid -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      @for (quiz of quizzes; track quiz._id) {
        <div
          class="daisy-card daisy-card-bordered bg-base-100 shadow-sm hover:shadow-md transition-all duration-300"
          [class.border-warning]="!isAuthorized"
          [class.bg-base-200/40]="!isAuthorized"
        >
          <div class="daisy-card-body p-4 sm:p-5 flex flex-col justify-between">
            <div>
              <div class="flex items-start gap-2.5 mb-2">
                <!-- Status indicator dot -->
                <div
                  class="w-3 h-3 rounded-full mt-1.5 shrink-0"
                  [class.bg-success]="isAuthorized"
                  [class.bg-warning]="!isAuthorized"
                  [title]="isAuthorized ? 'Accessible Quiz' : 'Restricted Quiz'"
                ></div>

                <div class="flex-1 min-w-0">
                  <div class="flex flex-wrap items-center gap-2">
                    <h4 class="font-bold text-base text-base-content line-clamp-1">
                      {{ quiz.title }}
                    </h4>
                    @if (!isAuthorized) {
                      @if (isPending) {
                        <span class="daisy-badge daisy-badge-warning daisy-badge-sm font-semibold gap-1">
                          <i class="fa-solid fa-clock text-xs"></i>
                          Verification Pending
                        </span>
                      } @else {
                        <span class="daisy-badge daisy-badge-ghost daisy-badge-sm font-semibold gap-1 border-warning/40 text-warning">
                          <i class="fa-solid fa-lock text-xs"></i>
                          Subscription Required
                        </span>
                      }
                    }
                  </div>
                </div>

                @if (isAuthorized && getBestScore(quiz._id) !== null) {
                  <span class="daisy-badge daisy-badge-primary daisy-badge-sm shrink-0">
                    Best: {{ getBestScore(quiz._id) }} / {{ quiz.totalMarks }}
                  </span>
                }
              </div>

              @if (quiz.description) {
                <p class="text-xs text-base-content/60 line-clamp-2 mb-3">
                  {{ quiz.description }}
                </p>
              }

              <div class="flex flex-wrap gap-2 text-xs text-base-content/70 my-3">
                <span class="daisy-badge daisy-badge-outline daisy-badge-sm gap-1">
                  <i class="fa-solid fa-list-check text-primary"></i>
                  {{ quiz.questions.length }} Questions
                </span>
                <span class="daisy-badge daisy-badge-outline daisy-badge-sm gap-1">
                  <i class="fa-solid fa-star text-warning"></i>
                  {{ quiz.totalMarks }} Marks
                </span>
                @if (quiz.durationMinutes) {
                  <span class="daisy-badge daisy-badge-outline daisy-badge-sm gap-1">
                    <i class="fa-solid fa-clock text-info"></i>
                    {{ quiz.durationMinutes }} mins
                  </span>
                }
              </div>

              @if (getWindowStatus(quiz); as winStatus) {
                <div class="text-xs p-2 rounded-lg my-2 flex items-center gap-1.5"
                  [class]="winStatus.status === 'open' ? 'bg-success/10 text-success' : (winStatus.status === 'upcoming' ? 'bg-info/10 text-info' : 'bg-error/10 text-error')">
                  <i [class]="winStatus.status === 'open' ? 'fa-solid fa-clock' : (winStatus.status === 'upcoming' ? 'fa-solid fa-calendar-clock' : 'fa-solid fa-lock')"></i>
                  <span>{{ winStatus.message }}</span>
                </div>
              }
            </div>

            <div class="pt-3 border-t border-base-200 mt-2 flex items-center justify-between">
              <span class="text-xs text-base-content/60">
                @if (isAuthorized) {
                  Attempts: {{ getAttemptsForQuiz(quiz._id).length }} / {{ quiz.maxAttempts || '1' }}
                } @else {
                  Max Attempts: {{ quiz.maxAttempts || '1' }}
                }
              </span>

              @if (isAuthorized) {
                <black-ui-modal>
                  <button
                    *blackModalTrigger="let toggle = toggle"
                    (click)="toggle(); startQuiz(quiz)"
                    class="daisy-btn daisy-btn-primary daisy-btn-sm gap-2"
                    [disabled]="!canAttempt(quiz)"
                  >
                    <i class="fa-solid fa-play text-xs"></i>
                    {{ getAttemptsForQuiz(quiz._id).length > 0 ? 'Retake Quiz' : 'Start Quiz' }}
                  </button>

                  <div
                    *blackModalContent="let close = close"
                    class="reveal-modal"
                    (click)="closeQuizModal(close)"
                  >
                    <div
                      class="reveal-modal-body rounded-2xl bg-base-100 max-w-3xl w-full p-4 sm:p-6 shadow-2xl overflow-y-auto max-h-[90vh] max-h-[90dvh]"
                      (click)="$event.stopPropagation()"
                    >
                    @if (showResult && latestSubmission) {
                      <!-- Quiz Result Screen -->
                      <div class="text-center py-6 space-y-4">
                        <div
                          class="w-16 h-16 sm:w-20 sm:h-20 rounded-full mx-auto flex items-center justify-center text-2xl sm:text-3xl"
                          [class]="latestSubmission.passed ? 'bg-success/20 text-success' : 'bg-error/20 text-error'"
                        >
                          <i [class]="latestSubmission.passed ? 'fa-solid fa-trophy' : 'fa-solid fa-circle-xmark'"></i>
                        </div>

                        <h3 class="text-xl sm:text-2xl font-bold text-base-content">
                          {{ latestSubmission.passed ? 'Great Job! You Passed!' : 'Quiz Completed' }}
                        </h3>

                        <div class="text-3xl sm:text-4xl font-extrabold text-primary">
                          {{ latestSubmission.totalScore }} / {{ activeQuiz?.totalMarks }}
                        </div>

                        <p class="text-xs sm:text-sm text-base-content/60">
                          Passing Marks: {{ activeQuiz?.passingMarks }} | Attempt #{{ latestSubmission.attemptNumber }}
                        </p>

                        <div class="pt-4 sm:pt-6">
                          <button
                            (click)="closeQuizModal(close)"
                            class="daisy-btn daisy-btn-primary px-8 w-full sm:w-auto"
                          >
                            Done
                          </button>
                        </div>
                      </div>
                    } @else if (activeQuiz) {
                      <!-- Quiz Player Header -->
                      <div class="flex items-center justify-between gap-3 pb-4 border-b border-base-200">
                        <div class="min-w-0 flex-1">
                          <h3 class="text-base sm:text-lg font-bold text-base-content break-words">
                            {{ activeQuiz.title }}
                          </h3>
                          <p class="text-xs text-base-content/60">
                            Question {{ activeQuestionIndex + 1 }} of {{ activeQuiz.questions.length }}
                          </p>
                        </div>

                        @if (activeQuiz.durationMinutes && activeQuiz.durationMinutes > 0) {
                          <div
                            class="daisy-badge daisy-badge-md sm:daisy-badge-lg font-mono gap-1.5 shrink-0"
                            [class]="remainingSeconds < 60 ? 'daisy-badge-error text-white' : 'daisy-badge-warning'"
                          >
                            <i class="fa-solid fa-stopwatch text-xs"></i>
                            <span class="text-xs sm:text-sm font-semibold">{{ formattedTime }}</span>
                          </div>
                        }
                      </div>

                      <!-- Question Content -->
                      @if (activeQuiz.questions[activeQuestionIndex]; as currentQ) {
                        <div class="py-4 sm:py-6 space-y-4 sm:space-y-6">
                          <div class="daisy-card daisy-card-bordered bg-base-200/50 p-3.5 sm:p-4 rounded-xl">
                            <div class="flex items-start justify-between gap-3">
                              <h4 class="text-sm sm:text-base font-semibold text-base-content break-words min-w-0 flex-1">
                                {{ activeQuestionIndex + 1 }}. {{ currentQ.question }}
                              </h4>
                              <span class="daisy-badge daisy-badge-sm daisy-badge-ghost shrink-0">
                                {{ currentQ.marks }} mark(s)
                              </span>
                            </div>
                          </div>

                          <!-- Options -->
                          <div class="space-y-2.5 sm:space-y-3">
                            @for (opt of currentQ.options; track opt.id) {
                              <div
                                (click)="selectOption(currentQ.id, opt.id)"
                                class="p-3 sm:p-4 rounded-xl border-2 transition-all cursor-pointer flex items-center gap-3"
                                [class]="isOptionSelected(currentQ.id, opt.id) ? 'border-primary bg-primary/5 text-primary font-medium' : 'border-base-300 hover:border-base-400 bg-base-100'"
                              >
                                <div
                                  class="w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0"
                                  [class]="isOptionSelected(currentQ.id, opt.id) ? 'border-primary bg-primary text-white' : 'border-base-400'"
                                >
                                  @if (isOptionSelected(currentQ.id, opt.id)) {
                                    <i class="fa-solid fa-check text-xs"></i>
                                  }
                                </div>
                                <span class="text-xs sm:text-sm break-words min-w-0 flex-1 leading-snug">{{ opt.text }}</span>
                              </div>
                            }
                          </div>
                        </div>

                        <!-- Quiz Navigation / Submit -->
                        <div class="flex items-center justify-between gap-2 pt-4 border-t border-base-200">
                          <button
                            (click)="prevQuestion()"
                            class="daisy-btn daisy-btn-ghost daisy-btn-sm gap-1.5 sm:gap-2"
                            [disabled]="activeQuestionIndex === 0"
                          >
                            <i class="fa-solid fa-arrow-left"></i>
                            <span>Previous</span>
                          </button>

                          <div class="flex items-center gap-2">
                            @if (activeQuestionIndex < activeQuiz.questions.length - 1) {
                              <button
                                (click)="nextQuestion()"
                                class="daisy-btn daisy-btn-primary daisy-btn-sm gap-1.5 sm:gap-2"
                              >
                                <span>Next</span>
                                <i class="fa-solid fa-arrow-right"></i>
                              </button>
                            } @else {
                              <button
                                (click)="submitQuizAnswers()"
                                class="daisy-btn daisy-btn-success text-white daisy-btn-sm gap-1.5 sm:gap-2"
                                [disabled]="submitting"
                              >
                                @if (submitting) {
                                  <span class="daisy-loading daisy-loading-spinner daisy-loading-xs"></span>
                                }
                                <i class="fa-solid fa-paper-plane"></i>
                                <span>Submit Quiz</span>
                              </button>
                            }
                          </div>
                        </div>
                      }
                    }
                    </div>
                  </div>
                </black-ui-modal>
            } @else {
              <button
                disabled
                class="daisy-btn daisy-btn-sm daisy-btn-ghost bg-base-200 cursor-not-allowed opacity-60 text-warning gap-1.5"
                title="Access Restricted - Requires Active Subscription"
              >
                <i class="fa-solid fa-lock text-xs"></i>
                <span>Locked</span>
              </button>
            }
            </div>
          </div>
        </div>
      }
    </div>
  }
</div>
